/**
 * Apex Six framework — canonical pre-game notecards.
 *
 * Mirrors the data in `/app/backend/scripts/build_workshop_decks.py::APEX_SIX`
 * so the on-site notecards stay in lockstep with the printed laminated card
 * set and trading-card decks. Each entry is framed for *pre-game use* even
 * for tools that are also useful in-game (3-Breath Reset, Bounce-Back 3R).
 *
 * `freeAccess` flag controls the Locker Room freemium gate:
 *   - true  → free tier visitors see the full card (steps, cue, the works)
 *   - false → free tier visitors see a teaser tile with the title, dimension,
 *             and an "Unlock with Apex Locker Room" CTA pointing at the
 *             Founders Waitlist modal.
 *
 * The 3 free cards (01, 02, 06) are the most universally pre-game-specific:
 * nerve management, confidence anchor, and the warm-up protocol. The 3 locked
 * cards (03, 04, 05) cover in-game awareness, visualization, and resilience —
 * deeper protocols that justify the upgrade.
 */

export const APEX_SIX_PREGAME_CARDS = [
  {
    number: '01',
    title: 'The 3-Breath Reset',
    dimension: 'COMPOSURE',
    freeAccess: true,
    pregameUse: 'When pre-game nerves spike or the bus ride starts spiraling.',
    steps: [
      { label: 'Exhale fully', detail: 'Force every bit of air out. Drop the shoulders.' },
      { label: 'Box-breathe', detail: 'In 4 · hold 4 · out 4 · hold 4. One cycle.' },
      { label: 'Power inhale + cue word', detail: 'Sharp inhale, lock in your one word.' },
    ],
    cue: '30 seconds. Nervous system reboot.',
  },
  {
    number: '02',
    title: 'The ABC Confidence Stack',
    dimension: 'CONFIDENCE',
    freeAccess: true,
    pregameUse: 'Pre-performance. Walk in feeling ready, not nervous.',
    steps: [
      { label: 'A — Anchor', detail: 'Physical reset: feet planted, posture tall, eyes up.' },
      { label: 'B — Belief', detail: 'Your one-line truth: "I\u2019ve done the work."' },
      { label: 'C — Cue', detail: 'Trigger word that flips the switch to compete mode.' },
    ],
    cue: '90-second ritual. Own it. Run it. Every time.',
  },
  {
    number: '03',
    title: 'Green-Yellow-Red Awareness',
    dimension: 'AWARENESS',
    freeAccess: false,
    pregameUse: 'Self-scan in the warm-up. Pre-game self-check before tip-off.',
    teaser: 'A traffic-light self-scan that lets you catch a drift before it becomes a spiral. The pro athlete\u2019s mental dashboard.',
    steps: [
      { label: 'GREEN — Locked in', detail: 'Stay the course. Keep your routines.' },
      { label: 'YELLOW — Drifting', detail: 'Run Bounce-Back 3R or one breath cycle.' },
      { label: 'RED — Spiraling', detail: 'Run the 3-Breath Reset immediately.' },
    ],
    cue: 'Catch yellow before it becomes red.',
  },
  {
    number: '04',
    title: 'The Camera Drill',
    dimension: 'FOCUS',
    freeAccess: false,
    pregameUse: 'Visualization the night before — or 10 min before warm-up.',
    teaser: 'First-person mental reps that hard-wire success before you ever step on the field. The same drill Iga Swiatek and Rory McIlroy use.',
    steps: [
      { label: 'First-person view', detail: 'See it through your own eyes, not from the stands.' },
      { label: 'Sensory-rich rehearsal', detail: 'Sound, smell, feel, the crowd, the ball, the moment.' },
      { label: '3 reps, successful outcome', detail: 'Never rehearse failure. Always finish the play.' },
    ],
    cue: 'Reps live in the mind first.',
  },
  {
    number: '05',
    title: 'Bounce-Back 3R',
    dimension: 'RESILIENCE',
    freeAccess: false,
    pregameUse: 'Pre-load this tool in the locker room. It\u2019s your in-game safety net.',
    teaser: 'Your 30-second recovery protocol for the play you wish you had back. Without this, one mistake becomes three.',
    steps: [
      { label: 'Release', detail: 'One physical reset. Exhale the mistake.' },
      { label: 'Refocus', detail: 'Eyes to the next moment. The past play is dead.' },
      { label: 'Re-engage', detail: 'Step in with intent. Body language sells it first.' },
    ],
    cue: '30 seconds. The next play is bigger than the last.',
  },
  {
    number: '06',
    title: 'The Performance Pyramid',
    dimension: 'PREPARATION',
    freeAccess: true,
    pregameUse: 'Pre-game when you feel scattered, rushed, or unprepared.',
    steps: [
      { label: 'Body', detail: 'Dynamic warm-up. Get the heart rate honest.' },
      { label: 'Breath', detail: 'Rhythm work. Sync breathing to your pace.' },
      { label: 'Belief', detail: 'Anchor your one-line truth and your cue word.' },
    ],
    cue: 'Body → Breath → Belief. In that exact order.',
  },
];

export default APEX_SIX_PREGAME_CARDS;
